'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
    generateFloatingHearts,
    hasColorTransition,
    HEART_COLORS,
    FloatingHeart,
    DEFAULT_ANIMATION_CONFIG,
} from './HeartBurstAnimationTypes';
import styles from './HeartBurstAnimation.module.scss';

const TOTAL_ANIMATION_DURATION_MS = Number(styles.totalDurationMs);

interface HeartBurstAnimationProps {
    /** Width of the container (default: '100%' - fills parent) */
    width?: number | string;
    /** Height of the container (default: 120) */
    height?: number;
    trigger?: boolean;
    /** Keep heart visible after animation (default: false) */
    persist?: boolean;
    onComplete?: () => void;
    mainHeartSize?: number;
    mainHeartColor?: string;
}

const getHeartFloatClass = (shrinkType?: FloatingHeart['shrinkType']) => {
    switch (shrinkType) {
        case 'tiny':
            return styles.heartFloatTiny;
        case 'small':
            return styles.heartFloatSmall;
        default:
            return styles.heartFloat;
    }
};

// SVG Heart Shape Component
const HeartShape: React.FC<{ color: string; size: number; opacity?: number }> = ({
    color,
    size,
    opacity = 1
}) => (
    <svg
        className={styles.heartShape}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={color}
        style={{ opacity }}
    >
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
);

export const HeartBurstAnimation: React.FC<HeartBurstAnimationProps> = ({
    width = '100%',
    height = 120,
    trigger = false,
    persist = true,
    onComplete,
    mainHeartSize = 100,
    mainHeartColor = HEART_COLORS.FILL_1,
}) => {
    const [phase, setPhase] = useState<'idle' | 'active'>('idle');
    const [floatingHearts, setFloatingHearts] = useState<FloatingHeart[]>([]);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const onCompleteRef = useRef(onComplete);

    // Measure container size on mount and when dimensions change
    useEffect(() => {
        const measureContainer = () => {
            if (containerRef.current) {
                const { offsetWidth, offsetHeight } = containerRef.current;
                setContainerSize({ width: offsetWidth, height: offsetHeight });
            }
        };

        measureContainer();
        // Also measure on resize
        window.addEventListener('resize', measureContainer);
        return () => window.removeEventListener('resize', measureContainer);
    }, [width, height]);

    useEffect(() => {
        if (!trigger) return;

        // Re-measure container when trigger fires
        if (containerRef.current) {
            const { offsetWidth, offsetHeight } = containerRef.current;
            setContainerSize({ width: offsetWidth, height: offsetHeight });
        }

        // Start animation - emit hearts immediately
        setPhase('active');
        setFloatingHearts(generateFloatingHearts());

        // Complete - only hide if not persisting
        const completeTimer = setTimeout(() => {
            if (!persist) {
                setPhase('idle');
                setFloatingHearts([]);
            }
            onCompleteRef.current?.();
        }, TOTAL_ANIMATION_DURATION_MS);

        return () => {
            clearTimeout(completeTimer);
        };
    }, [trigger, persist]);

    // Calculate heart size based on container width
    const containerWidth = containerSize.width;

    // Main heart fills 80% of container width
    const scaledMainHeartSize = Math.round(containerWidth * 0.8);

    // Floating hearts scale proportionally (relative to main heart)
    const getScaledSize = (size: number) => Math.round((size / mainHeartSize) * scaledMainHeartSize);

    // Calculate bounds to keep hearts within container
    const maxXDrift = (containerWidth / 2) * 0.6; // 60% of half-width
    const maxYDistance = height - scaledMainHeartSize; // Full available height
    const yDistanceMax = DEFAULT_ANIMATION_CONFIG.yDistanceMax ?? 650;

    // Constrain movement to stay within container bounds
    const constrainX = (x: number) => {
        const scaled = x * 0.1; // Scale down significantly
        return Math.max(-maxXDrift, Math.min(maxXDrift, scaled));
    };
    const constrainY = (y: number) => {
        const targetY = -maxYDistance * (Math.abs(y) / yDistanceMax);
        return Math.max(-maxYDistance, targetY);
    };

    return (
        <div
            ref={containerRef}
            className={styles.container}
            style={{width}}
        >
            {phase === 'active' && (
                <>
                    {/* Hearts emerging from the main heart center and floating upward */}
                    {floatingHearts.map((heart) => {
                        const shouldTransitionColor = hasColorTransition(heart);
                        const scaledHeartSize = getScaledSize(heart.size);
                        const scaledX = Math.round(constrainX(heart.x));
                        const scaledY = Math.round(constrainY(heart.y));

                        return (
                            <div
                                key={heart.id}
                                className={`${styles.heartContainer} ${getHeartFloatClass(heart.shrinkType)}`}
                                style={{
                                    bottom: scaledMainHeartSize / 2,
                                    animationDelay: `${heart.delay}ms`,
                                    '--hx': `${scaledX}px`,
                                    '--hy': `${scaledY}px`,
                                    zIndex: floatingHearts.length - heart.id,
                                } as React.CSSProperties}
                                aria-hidden="true"
                            >
                                {shouldTransitionColor && heart.colorTransition ? (
                                    <div className={styles.transition}>
                                        {/* Target color heart underneath (fades in at end) */}
                                        <div
                                            className={styles.heartFadeIn}
                                            style={{ animationDelay: `${heart.delay}ms` }}
                                        >
                                            <HeartShape color={heart.colorTransition.to} size={scaledHeartSize} />
                                        </div>
                                        {/* Source color heart on top (fades out at end) */}
                                        <div
                                            className={styles.heartFadeOut}
                                            style={{ animationDelay: `${heart.delay}ms` }}
                                        >
                                            <HeartShape color={heart.colorTransition.from} size={scaledHeartSize} />
                                        </div>
                                    </div>
                                ) : (
                                    <HeartShape color={heart.color} size={scaledHeartSize} />
                                )}
                            </div>
                        );
                    })}

                    {/* Main heart - single heart that stays visible */}
                    <div
                        className={`${styles.mainHeart} ${styles.mainHeartPulse}`}
                        aria-hidden="true"
                    >
                        <HeartShape color={mainHeartColor} size={scaledMainHeartSize} />
                    </div>
                </>
            )}

            {/* Static heart when trigger is false (no animation) */}
            {!trigger && phase === 'idle' && (
                <div
                    className={styles.mainHeart}
                    aria-hidden="true"
                >
                    <HeartShape
                        color={mainHeartColor}
                        size={scaledMainHeartSize || mainHeartSize}
                    />
                </div>
            )}
        </div>
    );
};

export default HeartBurstAnimation;
